import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";

export type CategoryLang = "en" | "ar" | "tr" | "de" | "ku";
export const CATEGORY_LANGS: CategoryLang[] = ["en", "ar", "tr", "de", "ku"];

export interface PortfolioCategory {
  id: string;
  slug: string;
  name_i18n: Partial<Record<CategoryLang, string>>;
  display_order: number;
}

export const PORTFOLIO_CATEGORIES_KEY = ["portfolio-categories"];

export function usePortfolioCategories() {
  const { i18n } = useTranslation();
  const lang = (i18n.language?.slice(0, 2) || "en") as CategoryLang;

  const query = useQuery({
    queryKey: PORTFOLIO_CATEGORIES_KEY,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("portfolio_categories")
        .select("id, slug, name_i18n, display_order")
        .order("display_order")
        .order("created_at");
      if (error) throw error;
      return (data ?? []) as PortfolioCategory[];
    },
    staleTime: 5 * 60 * 1000,
  });

  const bySlug = new Map((query.data ?? []).map((c) => [c.slug, c]));

  /** Translated label for a stored category value; falls back to the raw value for legacy entries. */
  const label = (slug: string) => {
    const c = bySlug.get(slug);
    if (!c) return slug;
    return c.name_i18n[lang]?.trim() || c.name_i18n.en?.trim() || slug;
  };

  return { ...query, categories: query.data ?? [], label, lang };
}
